import { create } from 'zustand';
import {
  addReport,
  getReports,
  getReport,
  updateReport,
  deleteReport,
  approveReport,
  rejectReport,
  submitClientDecision,
} from '../services/report.service';
import { Report, CreateReport, ReportDetails, SubmitClientDecisionRequest } from '../types/report.type';

interface ReportState {
  reports: Report[];
  report: ReportDetails | null;
  isLoading: boolean;
  error: string | null;
  fetchReports: (organizationId: string, params?: any) => Promise<void>;
  fetchReport: (reportId: string) => Promise<void>;
  addReport: (data: CreateReport) => Promise<ReportDetails>;
  updateReport: (reportId: string, data: { data: any }) => Promise<void>;
  deleteReport: (reportId: string) => Promise<void>;
  approveReport: (reportId: string) => Promise<void>;
  rejectReport: (reportId: string, reason?: string) => Promise<void>;
  submitClientDecision: (reportId: string, data: SubmitClientDecisionRequest) => Promise<ReportDetails>;
}

export const useReportStore = create<ReportState>((set) => ({
  reports: [],
  report: null,
  isLoading: false,
  error: null,

  fetchReports: async (organizationId, params) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getReports(organizationId, params);
      if (response?.result_list) {
        set({ reports: response.result_list, isLoading: false });
      } else if (Array.isArray(response)) {
        set({ reports: response, isLoading: false });
      } else {
        set({ reports: [], isLoading: false });
      }
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error fetching reports';
      set({ error: message, isLoading: false, reports: [] });
    }
  },

  fetchReport: async (reportId: string) => {
    set({ isLoading: true, error: null });
    try {
      const response = await getReport(reportId);
      set({ report: response, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error fetching report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  addReport: async (data: CreateReport): Promise<ReportDetails> => {
    set({ isLoading: true, error: null });
    try {
      const created: ReportDetails = await addReport(data);
      set({ isLoading: false });
      return created;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error creating report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  updateReport: async (reportId: string, data: { data: any }) => {
    set({ isLoading: true, error: null });
    try {
      const updated: ReportDetails = await updateReport(reportId, data);
      set({ report: updated, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error updating report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  deleteReport: async (reportId: string) => {
    set({ isLoading: true, error: null });
    try {
      await deleteReport(reportId);
      set((state) => ({
        reports: state.reports.filter((r) => r.id !== reportId),
        isLoading: false,
      }));
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error deleting report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  approveReport: async (reportId: string) => {
    set({ isLoading: true, error: null });
    try {
      const updated: ReportDetails = await approveReport(reportId);
      set({ report: updated, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error approving report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  rejectReport: async (reportId: string, reason?: string) => {
    set({ isLoading: true, error: null });
    try {
      const updated: ReportDetails = await rejectReport(reportId, reason);
      set({ report: updated, isLoading: false });
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error rejecting report';
      set({ error: message, isLoading: false });
      throw error;
    }
  },

  submitClientDecision: async (reportId: string, data: SubmitClientDecisionRequest) => {
    set({ error: null });
    try {
      const updated: ReportDetails = await submitClientDecision(reportId, data);
      set((state) => ({
        report: state.report?.id === reportId ? updated : state.report,
        reports: state.reports.map((r) => (
          r.id === reportId
            ? {
              ...r,
              client_decision: updated.client_decision,
              client_decision_at: updated.client_decision_at,
              client_note: updated.client_note,
            }
            : r
        )),
      }));
      return updated;
    } catch (error: any) {
      const message = error.response?.data?.message || error.message || 'Error submitting client decision';
      set({ error: message });
      throw error;
    }
  },
}));
